import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useToast } from "hooks/useToast";
import { useCreateFormTemplateMutation } from "store/apis/incorporationFormBuilder";
import { BackendError } from "types/utils/error";
import { useHistory } from "react-router-dom";
import * as yup from "yup";
import Input from "components/DesignSystem/Input/Input";

type FormValues = {
  title: string;
};

const validationSchema = yup.object({
  title: yup.string().required("Template name is required"),
});

type CreateTemplateModalProps = {
  isOpen: boolean;
  close: () => void;
};

export const CreateTemplateModal = ({
  isOpen,
  close,
}: CreateTemplateModalProps) => {
  const history = useHistory();
  const { alertToast } = useToast();
  const [createBaseTemplate, { isLoading }] = useCreateFormTemplateMutation();

  const onSubmit = async (values: FormValues) => {
    try {
      const template = await createBaseTemplate({
        title: values.title,
      }).unwrap();
      close();
      history.push(`/admin/incorporation/templates/${template.uuid}?edit=true`);
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content asChild>
        <FormikForm<FormValues>
          initialValues={{ title: "" }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ values }) => (
            <>
              <Modal.Header>
                <Modal.Title>Add new template</Modal.Title>
                <Modal.Close />
              </Modal.Header>

              <Modal.Body>
                <Input
                  withForm
                  name="title"
                  label="Template name"
                  required
                  placeholder="e.g. LLP → US Entity → India Subs"
                />
              </Modal.Body>

              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button">Cancel</Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  type="submit"
                  isLoading={isLoading}
                  disabled={isLoading || !values.title}
                >
                  Create
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
